// Order pushes about a Wolt delivery or a refund — which ones the web knows,
// what each one tells the guest, and whether it is good or bad news.
//
// Mirrors the app (hey_waitr hooks/delivery/push.ts and hooks/orders/
// refunded.ts), so a guest is told the same thing on both. The backend sends
// these with `data: {type, orderid}` and its own title/body, written in Danish
// or English only; the notice uses OUR copy instead, in the page's language.
//
// Copy rule: a failed delivery does not always mean the guest was refunded, so
// none of the failure texts promise a refund. Only a refund says so.

import { WOLT_NO_COURIER, refundMessageKey, woltFailureKind } from './woltDelivery';

export const WOLT_PUSH = {
  wolt_pickup_started: { messageKey: 'wolt_push_pickup_started', variant: 'info' },
  wolt_picked_up: { messageKey: 'wolt_push_picked_up', variant: 'info' },
  wolt_rejected: { messageKey: 'wolt_rejected', variant: 'failure' },
  wolt_cancelled: { messageKey: 'wolt_push_cancelled', variant: 'failure' },
  wolt_customer_no_show: { messageKey: 'wolt_push_customer_no_show', variant: 'failure' },
};

export const isWoltPushType = (type) =>
  typeof type === 'string' && Object.prototype.hasOwnProperty.call(WOLT_PUSH, type);

/**
 * The refund a push or a status answer carries, or null.
 *
 *   order_refunded             checkstatus, or the venue's manual refund push
 *   wolt_rejected refunded     Wolt refused a held order and the backend
 *                              refunded it (notifyGuestWoltUndeliverable)
 *
 * refunded "false" means the refund was left for a human — the money is not
 * back yet — and the older wolt_rejected carries no flag at all: both are a
 * plain delivery failure, not a refund.
 */
export const getOrderRefund = (data) => {
  const orderId = data?.orderid;
  if (!orderId) return null;

  if (data.type === 'order_refunded') {
    return { orderId: String(orderId), reason: data.reason ?? '' };
  }
  if (data.type === 'wolt_rejected' && String(data.refunded) === 'true') {
    return { orderId: String(orderId), reason: WOLT_NO_COURIER };
  }
  return null;
};

/**
 * The push a failed delivery would have sent, for when the order poller sees
 * the failure first — most web guests never allow push. A failure with no
 * Wolt status at all (woltDeliveryState 'failed': the booking never landed)
 * reads as the rejection it is.
 */
export const woltFailurePushType = (status) => {
  if (status?.woltDeliveryStatus === 'customer_no_show') return 'wolt_customer_no_show';
  return woltFailureKind(status) === 'cancelled' ? 'wolt_cancelled' : 'wolt_rejected';
};

// A notice is shown once per key. A push can arrive twice, and the order
// poller reports the same failure or refund a few seconds after its push, so
// the three failures share one key per order and a refund has its own. No
// order id, no key: like the app, only a push about an order is de-duplicated.
export const woltNoticeKey = (type, orderId) =>
  orderId ? `${WOLT_PUSH[type]?.variant === 'failure' ? 'wolt_failed' : type}:${orderId}` : null;

export const refundNoticeKey = (orderId) => `refunded:${orderId}`;

// Notification-modal entries. The text is named by key and looked up when the
// modal renders, so it reads in the language the page is in at that moment.
export const woltPushNotice = (type, orderId) => ({
  messageKey: WOLT_PUSH[type].messageKey,
  variant: WOLT_PUSH[type].variant,
  data: { type, orderid: orderId },
});

// The order a push is about: status pushes carry it as `orderid`, the
// completion push also as `id` (the app's pushOrderId).
export const pushOrderId = (data) => data?.orderid || data?.id || undefined;

export const refundNotice = (reason, orderId) => ({
  messageKey: refundMessageKey(reason),
  variant: 'failure',
  data: { type: 'order_refunded', orderid: orderId },
});
